// Single source of truth for which image formats the apps accept on upload.
// PNG, JPEG, WebP, AVIF, SVG and GIF. JPEG was excluded by an early product
// rule; what that rule did in practice was refuse every photo a phone camera
// takes, so a product image could not be added from the device standing in
// the kitchen. Used for the file-picker `accept` attribute and for client-side
// validation in `uploadMedia`. The backend (`/media/upload`) enforces the same
// set as the real gate.

export const ALLOWED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/avif',
  'image/svg+xml',
  'image/gif',
] as const

export const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'avif', 'svg', 'gif'] as const

/** `accept` attribute value for image file inputs — MIME types plus extensions
 *  (some browsers report an empty/odd MIME for SVG/AVIF, so list both). */
export const ACCEPT_IMAGE =
  'image/png,image/jpeg,image/webp,image/avif,image/svg+xml,image/gif,.png,.jpg,.jpeg,.webp,.avif,.svg,.gif'

/** Whether a picked file is an allowed image. Non-image files (video, fonts,
 *  pdf) are NOT this function's concern — it returns false only for images of a
 *  disallowed type, and true for anything that isn't a recognised image so the
 *  caller can let other media through. */
export function isAllowedImageType(file: File): boolean {
  const type = (file.type || '').toLowerCase()
  if (type.startsWith('image/')) {
    return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(type)
  }
  // No / non-image MIME reported — fall back to the extension so a disallowed
  // image with a stripped MIME (e.g. a `.jpg` dragged in) is still caught,
  // while genuine non-image uploads pass.
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  const imageExts = ['png', 'webp', 'avif', 'svg', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'heic', 'heif']
  if (imageExts.includes(ext)) {
    return (ALLOWED_IMAGE_EXTENSIONS as readonly string[]).includes(ext)
  }
  return true
}
